<template>
  <div v-if="isStoreReady">
    <card title="Filtros">
      <div class="row mt-n2">
        <SearchFieldSelector
          :default-start-date="computedStartDate"
          :default-end-date="computedEndDate"
          :options="dateSelectOptions"
          class="search-date-select-options"
          :clean-date="cleanDate"
          @updateSearchValue="onChangeDate"
        />

        <SearchFieldSelector
          class="search-multiple-text"
          :options="selectOptions"
          @updateSearchValue="setMultipleTextSearch"
        />

        <md-button
          class="btn btn-primary"
          @click="submitSearch"
        >
          <svg-loader :src="'/static/images/icon-generate-list.svg'" />
          Listar
        </md-button>
      </div>
    </card>
  </div>
</template>

<script>
import moment from 'moment'
import { mapActions, mapGetters } from 'vuex'
import Card from '@/components/common/Card.vue'
import SearchFieldSelector from '@/components/common/SearchFieldSelector.vue'
import { PENDENCIES_PERIOD_DAYS_DEFAULT } from '@/enums'
import notify from '../../api/notification'

export default {
  name: 'PendenciesFilter',

  components: {
    Card,
    SearchFieldSelector
  },

  data() {
    return {
      today: new Date(),
      dateSelected: { start: this.today, end: this.today },
      dateSelectOptions: [
        {
          itemValue: 'TD',
          value: 'transferDate',
          label: 'Período',
          placeholder: 'Período',
          limitDays: 1827,
          type: 'date'
        }
      ],
      selectOptions: [
        {
          value: 'PN',
          label: 'Nome do Paciente',
          placeholder: 'Nome do Paciente'
        },
        {
          value: 'OA',
          label: 'OS Alvaro',
          placeholder: 'OS Alvaro'
        }
      ],
      searchField: null,
      searchQuery: null,
      cleanDate: 0
    }
  },

  computed: {
    ...mapGetters(['pendenciesParams', 'pendencyDefaultStartDate', 'pendencyDefaultEndDate']),

    isStoreReady() {
      return this.pendenciesParams.startDate && this.pendenciesParams.endDate
    },

    computedStartDate() {
      const routeStartDate = this.$route.query.startDate
      return routeStartDate ? moment(routeStartDate, 'DD/MM/YYYY').toDate() : this.pendencyDefaultStartDate
    },

    computedEndDate() {
      const routeEndDate = this.$route.query.endDate
      return routeEndDate ? moment(routeEndDate, 'DD/MM/YYYY').toDate() : this.pendencyDefaultEndDate
    },

    pendenciesFilters() {
      const { start, end } = this.dateSelected
      const { status, orderBy } = this.pendenciesParams
      return {
        ...this.pendenciesParams,
        startDate: start ? moment(start).format('DD/MM/YYYY') : undefined,
        endDate: end ? moment(end).format('DD/MM/YYYY') : undefined,
        typeOfSearch: !this.searchQuery ? null : this.searchField,
        q: this.searchQuery == '' ? undefined : this.searchQuery,
        status,
        orderBy,
        offset: 0
      }
    }
  },

  async mounted() {
    this.setFiltersFromRoute()
    await this.getAllTotalPendenciesQuantity()
  },

  methods: {
    ...mapActions([
      'selectPendenciesFilter',
      'getPendencies',
      'getAllTotalPendenciesQuantity',
      'setWaitingPendenciesLoadingStatus'
    ]),

    setFiltersFromRoute() {
      const routeFilters = this.$route.query

      let startDate = routeFilters.startDate
        ? moment(routeFilters.startDate, 'DD/MM/YYYY').toDate()
        : this.pendencyDefaultStartDate

      let endDate = routeFilters.endDate
        ? moment(routeFilters.endDate, 'DD/MM/YYYY').toDate()
        : this.pendencyDefaultEndDate

      if (!this.isRangeValid({ start: startDate, end: endDate })) {
        startDate = this.pendencyDefaultStartDate
        endDate = this.pendencyDefaultEndDate

        notify({
          title: 'AVISO:',
          message: `O período selecionado foi ajustado para um intervalo de até ${PENDENCIES_PERIOD_DAYS_DEFAULT} dias.`
        })

        this.$router.replace({
          query: {
            ...routeFilters,
            startDate: moment(startDate).format('DD/MM/YYYY'),
            endDate: moment(endDate).format('DD/MM/YYYY')
          }
        })
      }

      this.dateSelected = { start: startDate, end: endDate }
      this.cleanDate++

      this.searchField = routeFilters.typeOfSearch ?? this.pendenciesParams.typeOfSearch ?? null
      this.searchQuery = routeFilters.q ?? this.pendenciesParams.q ?? null

      const updatedFilters = {
        ...this.pendenciesParams,
        startDate: moment(this.dateSelected.start).format('DD/MM/YYYY'),
        endDate: moment(this.dateSelected.end).format('DD/MM/YYYY'),
        status: routeFilters.status ?? this.pendenciesParams.status,
        orderBy: routeFilters.orderBy ?? this.pendenciesParams.orderBy
      }

      this.selectPendenciesFilter(updatedFilters)
    },

    onChangeDate({ date }) {
      if (this.isRangeValid(date)) {
        this.dateSelected = date
      } else {
        this.dateSelected = {
          start: this.pendencyDefaultStartDate,
          end: this.pendencyDefaultEndDate
        }
        this.cleanDate++

        notify({
          title: 'AVISO:',
          message: `Selecione um período de até ${PENDENCIES_PERIOD_DAYS_DEFAULT} dias.`
        })
      }
    },

    isRangeValid(date) {
      if (!date.start || !date.end) return false

      const start = moment(date.start)
      const end = moment(date.end)
      return end.diff(start, 'days') <= PENDENCIES_PERIOD_DAYS_DEFAULT
    },

    setMultipleTextSearch(e) {
      this.searchField = e?.value ?? null
      this.searchQuery = e?.query ?? null
    },

    async submitSearch() {
      if (!this.dateSelected.start || !this.dateSelected.end) {
        notify({
          title: 'Erro:',
          message: 'Por favor, selecione um período válido antes de buscar.'
        });
        return;
      }

      if (this.searchField === 'OA' && (isNaN(this.searchQuery))) {
        notify({
          title: 'Aviso:',
          message: 'O campo "OS Alvaro" deve conter apenas números.'
        });
        this.searchQuery = '';
        return;
      }

      this.selectPendenciesFilter(this.pendenciesFilters);
      await this.$store.dispatch('fetchPendenciesForSubmit');
      await this.getAllTotalPendenciesQuantity();
    }
  }
}
</script>

<style lang="scss" scoped>
:deep(.card-form__container) {
  padding-top: 8px !important;
}

.w-100 {
  width: 100%;
}

.row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  width: 100%;

  div {
    flex: 1 1 auto;
    margin: 0;
  }
}

.search-date-select-options {
  max-width: 30%;
}
</style>
